import { useApolloClient } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isEmpty } from '@newfront-insurance/core';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMarkChatAsRead } from '../../../hooks';
import type { NovaSettingsData, NovaSettingField } from '../../../hooks/use-nova-settings';
import { GET_CHATS_BY_FILTERS, type ChatAppType } from '../../../queries/chats';
import { browserNotificationManager } from '../../../services/browser-notification-manager';
import { isTabAndWindowVisible, shouldShowBrowserNotification } from '../lib/notification-decision';
import { buildNotificationText, normalizeChatUuid, updateChatsListAfterStream } from '../lib/notification-helpers';

interface StreamRegistration {
  chatUuid: string;
  isChatVisible: boolean;
  isStandaloneMode: boolean;
}

export interface ChatNotificationController {
  // State
  hasNotifications: boolean;
  browserNotificationsEnabled: boolean;
  browserNotificationPermission: NotificationPermission;
  browserNotificationsSupported: boolean;

  // Actions
  setHasNotifications: (has: boolean) => void;
  enableBrowserNotifications(): Promise<boolean>;
  disableBrowserNotifications(): void;
  clearNotifications(): void;

  // Stream lifecycle
  registerStream(chatUuid: string, isChatVisible: boolean, isStandaloneMode: boolean): void;
  unregisterStream(chatUuid: string): void;
  onStreamFinish(params: {
    chatUuid: string;
    isChatVisible?: boolean;
    isStandaloneMode?: boolean;
    currentChatUuid?: string;
  }): Promise<void>;
  onOrphanedStreamFinish(params: { chatUuid: string; isStandaloneMode?: boolean }): Promise<void>;
}

export function useChatNotificationController({
  userUuid,
  currentChatUuid,
  effectiveAppType,
  serverSettings,
  onPersist,
  isPublicMode = false,
}: {
  userUuid?: string;
  currentChatUuid?: string;
  effectiveAppType?: ChatAppType | null;
  serverSettings?: NovaSettingsData | null;
  onPersist?: (field: NovaSettingField, value: unknown) => void;
  isPublicMode?: boolean;
}): ChatNotificationController {
  const apolloClient = useApolloClient();
  const isNovaNotificationsEnabled = useFeatureFlag('nova-notifications', false);
  const [hasNotifications, setHasNotifications] = useState(false);
  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] = useState(false);
  const isInitialized = useRef(false);
  const streamRegistrations = useRef<Map<string, StreamRegistration>>(new Map());
  const markChatAsRead = useMarkChatAsRead(userUuid, effectiveAppType);

  // Query chats to check for unread messages
  const { data } = useQuery(GET_CHATS_BY_FILTERS, {
    variables: {
      userUuid,
      appType: effectiveAppType,
    },
    skip: isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
  });

  const { chatsList: chats = [] } = data ?? {};

  // Initialize browser notification state from server settings or local state
  useEffect(() => {
    if (userUuid && !isInitialized.current) {
      isInitialized.current = true;
      const enabled = serverSettings?.notificationsEnabled ?? browserNotificationManager.isEnabled();
      setBrowserNotificationsEnabled(enabled);
      browserNotificationManager.setFeatureFlagEnabled(isNovaNotificationsEnabled);
    }
  }, [userUuid, isNovaNotificationsEnabled, serverSettings]);

  // Sync from server settings when they arrive
  useEffect(() => {
    if (serverSettings != null) {
      setBrowserNotificationsEnabled(serverSettings.notificationsEnabled);
    }
  }, [serverSettings]);

  // Check for unread messages when chats data changes
  useEffect(() => {
    const hasUnreadMessages = chats.some((chat) => chat.hasUnreadMessages);
    setHasNotifications(hasUnreadMessages);
  }, [chats]);

  // Browser notification actions
  const enableBrowserNotifications = useCallback(async (): Promise<boolean> => {
    try {
      const permission = await browserNotificationManager.requestPermission();
      if (permission === 'granted') {
        browserNotificationManager.enable();
        setBrowserNotificationsEnabled(true);
        onPersist?.('notificationsEnabled', true);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [onPersist]);

  const disableBrowserNotifications = useCallback(() => {
    browserNotificationManager.disable();
    setBrowserNotificationsEnabled(false);
    onPersist?.('notificationsEnabled', false);
  }, [onPersist]);

  const clearNotifications = useCallback(() => {
    browserNotificationManager.clearNotifications();
  }, []);

  // Stream lifecycle
  const registerStream = useCallback((chatUuid: string, isChatVisible: boolean, isStandaloneMode: boolean) => {
    streamRegistrations.current.set(chatUuid, {
      chatUuid,
      isChatVisible,
      isStandaloneMode,
    });

    browserLogger.debug('[Notification Controller] Registered stream', {
      chatUuid,
      isChatVisible,
      isStandaloneMode,
      activeStreams: streamRegistrations.current.size,
    });
  }, []);

  const unregisterStream = useCallback((chatUuid: string) => {
    const existed = streamRegistrations.current.has(chatUuid);
    streamRegistrations.current.delete(chatUuid);

    if (existed) {
      browserLogger.debug('[Notification Controller] Unregistered stream', {
        chatUuid,
        activeStreams: streamRegistrations.current.size,
      });
    }
  }, []);

  const onStreamFinish = useCallback(
    async ({
      chatUuid,
      isChatVisible: passedIsChatVisible,
      isStandaloneMode: passedIsStandaloneMode,
      currentChatUuid: passedCurrentChatUuid,
    }: {
      chatUuid: string;
      isChatVisible?: boolean;
      isStandaloneMode?: boolean;
      currentChatUuid?: string;
    }) => {
      if (isPublicMode) {
        unregisterStream(chatUuid);
        return;
      }

      const registration = streamRegistrations.current.get(chatUuid);
      if (!registration) {
        browserLogger.debug('[Notification Controller] Stream finished but no registration found', { chatUuid });
      }

      // Use stored values from registration time, with fallback to passed values
      const effectiveIsChatVisible = passedIsChatVisible ?? registration?.isChatVisible ?? false;
      const effectiveIsStandaloneMode = passedIsStandaloneMode ?? registration?.isStandaloneMode ?? false;
      const effectiveCurrentChatUuid = passedCurrentChatUuid ?? currentChatUuid;
      const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);
      const isCurrentChat =
        effectiveCurrentChatUuid !== undefined && normalizeChatUuid(effectiveCurrentChatUuid) === chatUuidWithoutPrefix;

      // Update Apollo cache
      updateChatsListAfterStream({
        apolloClient,
        chatUuid,
        userUuid: userUuid ?? '',
        appType: effectiveAppType,
        hasUnreadMessages: true,
      });

      // Mark chat as read if it's the current chat
      if (isCurrentChat && effectiveIsChatVisible) {
        markChatAsRead(chatUuidWithoutPrefix);
      }

      // Build notification text
      const notificationText = await buildNotificationText({
        apolloClient,
        chatUuid,
      });

      const tabAndWindowVisible = isTabAndWindowVisible();

      // Determine if we should show browser notification
      const shouldShow = shouldShowBrowserNotification({
        isChatVisible: effectiveIsChatVisible,
        isStandaloneMode: effectiveIsStandaloneMode,
        isCurrentChat,
        isOrphanedStream: false,
      });

      // Show browser notification if enabled and conditions are met
      if ((browserNotificationManager.isEnabled() && shouldShow) || !tabAndWindowVisible) {
        // Calculate forceShow: show notification even if tab/window is visible when:
        // - Chat is minimized (user can't see it)
        // - In standalone mode (user might have switched chats)
        const forceShow = !effectiveIsChatVisible || effectiveIsStandaloneMode;

        browserLogger.debug('[Notification Controller] Showing browser notification', {
          chatUuid,
          notificationText,
          forceShow,
          tabAndWindowVisible,
          effectiveIsChatVisible,
          effectiveIsStandaloneMode,
        });

        browserNotificationManager.showChatNotification(notificationText, chatUuid);
      } else {
        browserLogger.debug('[Notification Controller] Skipping browser notification', {
          chatUuid,
          shouldShow,
          isEnabled: browserNotificationManager.isEnabled(),
          effectiveIsChatVisible,
          effectiveIsStandaloneMode,
          isCurrentChat,
        });
      }

      // Update notification badge state
      setHasNotifications(true);

      // Clean up registration
      unregisterStream(chatUuid);
    },
    [apolloClient, userUuid, currentChatUuid, markChatAsRead, unregisterStream, effectiveAppType, isPublicMode],
  );

  const onOrphanedStreamFinish = useCallback(
    async ({ chatUuid }: { chatUuid: string }) => {
      if (isPublicMode) {
        return;
      }

      browserLogger.info('[Notification Controller] Handling orphaned stream finish', { chatUuid });

      updateChatsListAfterStream({
        apolloClient,
        chatUuid,
        userUuid: userUuid ?? '',
        appType: effectiveAppType,
        hasUnreadMessages: true,
      });

      const notificationText = await buildNotificationText({
        apolloClient,
        chatUuid,
      });

      if (browserNotificationManager.isEnabled()) {
        browserLogger.debug('[Notification Controller] Showing browser notification for orphaned stream', {
          chatUuid,
          notificationText,
        });

        browserNotificationManager.showChatNotification(notificationText, chatUuid);
      }

      setHasNotifications(true);
    },
    [apolloClient, userUuid, effectiveAppType, isPublicMode],
  );

  return useMemo(
    () => ({
      hasNotifications,
      browserNotificationsEnabled,
      browserNotificationPermission: browserNotificationManager.getPermission(),
      browserNotificationsSupported: browserNotificationManager.isSupported(),
      setHasNotifications,
      enableBrowserNotifications,
      disableBrowserNotifications,
      clearNotifications,
      registerStream,
      unregisterStream,
      onStreamFinish,
      onOrphanedStreamFinish,
    }),
    [
      hasNotifications,
      browserNotificationsEnabled,
      setHasNotifications,
      enableBrowserNotifications,
      disableBrowserNotifications,
      clearNotifications,
      registerStream,
      unregisterStream,
      onStreamFinish,
      onOrphanedStreamFinish,
    ],
  );
}
